import { Component } from 'react'
import ReactDOM from 'react-dom'
import { observer } from 'mobx-react'
import { browserHistory } from 'react-router'
import HistoryTracker from 'back-forward-history'

import * as h from 'h'

import IntellectualPropertyModel from 'models/IntellectualPropertyModel'
import CompanyModel from 'models/CompanyModel'
import ResourceModel from 'models/ResourceModel'
import StoryModel from 'models/StoryModel'
import FacultyModel from 'models/FacultyModel'
import NotFoundModel from 'models/NotFoundModel'

import IntellectualPropertyView from 'components/IntellectualPropertyView'
import CompanyView from 'components/CompanyView'
import ResourceView from 'components/ResourceView'
import StoryView from 'components/StoryView'
import FacultyView from 'components/FacultyView'
import NotFoundView from 'components/NotFoundView'

import { openPopout, closePopout, getFirstURLComponent } from 'utils'

import styles from './styles.scss'

// in the interest of keeping the popout generic,
// here we have adapters that match an instance of a model
// then create the inside view (ex. ProjectView)
// based on that particular instance, so that Popout
// doesn't really need to know anything about what
// a project needs, or what a company needs, etc.

@observer(['UiStore'])
export default class Popout extends Component {
  componentDidMount () {
    const type = getFirstURLComponent(this.props.location.pathname)
    openPopout(type, this.props.params.id)

    // This prevents initial popout being tagged to a new popout on a related
    // resource when full refreshing (hitting URL bar and then go).
    if (!HistoryTracker.getKeyForTag('initial popout')) {
      HistoryTracker.setTagForKey(this.props.location.key, 'initial popout')
    }
  }

  /*
   * Called when navigating from /slug/:id1 to /slug/:id2. Usually due to
   * clicking on a related resource.
   */
  componentWillReceiveProps (newProps) {
    if (newProps.params.id === this.props.params.id) return

    // Make page blank until new element is loaded. We could add a loader in the
    // future as well.
    this.props.UiStore.popoutElement = null
    const type = getFirstURLComponent(newProps.location.pathname)
    openPopout(type, newProps.params.id)
  }

  componentDidUpdate () {
    // react-router makes no assumptions about behavior and only updates
    // Popout's props. We want to emulate new page behavior by scrolling to the
    // top of the page after loading.
    ReactDOM.findDOMNode(this.refs.inner).scrollTop = 0
  }

  componentWillUnmount () {
    // Hitting the back button while the Popup is open will remove the Popout
    // component from the DOM, but not clear popoutElement. This leaves the page
    // scroll locked. Clear the popoutElement here during unmount.
    this.props.UiStore.popoutElement = null

    // For good measure. Also to make componentDidMount's tag conditional work.
    HistoryTracker.deleteTag('initial popout')
  }

  // --------------------------------------------------------------------------
  // Actions
  // --------------------------------------------------------------------------

  close = () => {
    closePopout()
  }

  goBack = () => {
    browserHistory.goBack()
  }

  // --------------------------------------------------------------------------
  // Helpers
  // --------------------------------------------------------------------------

  get matched () {
    const { UiStore } = this.props
    const { popoutElement: element } = UiStore

    if (!element) {
      return null
    }

    const matchers = [
      // model to match, matching view to use, predicate for 'x' close button type, predicate for aside element
      [ IntellectualPropertyModel, IntellectualPropertyView, () => false ],
      [ CompanyModel, CompanyView, ({image}) => image && image.details ],
      [ ResourceModel, ResourceView, () => false ],
      [ StoryModel, StoryView, () => false ],
      [ FacultyModel, FacultyView, () => true ],
      [ NotFoundModel, NotFoundView, () => false ]
    ]

    // find the appropriate adapter for this element
    for (let matcher of matchers) {
      const [ model, view, closePredicate ] = matcher

      if (element instanceof model) {
        return {
          content: h.h(view, { element }),
          closeType: closePredicate(element) ? 'out-black' : 'in-white'
        }
      }
    }

    return null
  }

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  render () {
    const matched = this.matched

    return h.div({ key: 'popout', ...h.ca(styles.popout) },
      h.div({ref: 'inner', ...h.ca(styles.inner)},
        h.z({ onClick: this.close, ...h.ca(styles.closeZone) }),
        h.div(h.ca(styles.content, { [styles.hasSideContent]: matched && matched.hasSideContent }),
          h.z({ onClick: this.close, ...h.ca(styles.closeButton, styles[matched && matched.closeType]), name: 'close' }, '×'),
          matched && matched.content)))
  }
}



// WEBPACK FOOTER //
// ./resources/assets/js/components/Popout/index.js